import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const backendUrl = process.env.REACT_APP_BACKEND_URL?.trim();
if (!backendUrl) throw new Error('REACT_APP_BACKEND_URL is required');
const client = axios.create({ baseURL: backendUrl, timeout: 30000 });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const getToken = useCallback(() => localStorage.getItem('portal_token'), []);
  const logout = useCallback(() => {
    localStorage.removeItem('portal_token');
    setUser(null);
    setAuthError(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const token = getToken();
    if (!token) return null;
    setLoading(true);
    setAuthError(null);
    try {
      const response = await client.get('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } });
      setUser(response.data);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) logout();
      else setAuthError('Uw account kon tijdelijk niet worden geladen. Probeer het opnieuw.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [getToken, logout]);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=') || !getToken()) {
      setLoading(false);
      return;
    }
    refreshUser().finally(() => setLoading(false));
  }, [getToken, refreshUser]);

  const login = useCallback(async (email, password) => {
    const response = await client.post('/api/auth/login', { email, password });
    localStorage.setItem('portal_token', response.data.token);
    setAuthError(null);
    setUser(response.data.user);
    return response.data.user;
  }, []);

  const api = useCallback((method, url, data = null, extraConfig = {}) => {
    const { headers: extraHeaders, ...restConfig } = extraConfig;
    const config = { headers: { Authorization: `Bearer ${getToken()}`, ...extraHeaders }, ...restConfig };
    if (method === 'get' || method === 'delete') return client[method](url, config);
    return client[method](url, data, config);
  }, [getToken]);

  const value = useMemo(() => ({ user, loading, authError, login, logout, api, getToken, refreshUser }),
    [user, loading, authError, login, logout, api, getToken, refreshUser]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);